import React, { useState } from 'react'
import { Drawer, Tabs, DatePicker, Dropdown, Menu } from 'antd'
import { useSelector } from 'react-redux'
import { Switch, Route, Link, useHistory, useParams } from 'react-router-dom'
import pluralize from 'pluralize' 

import { AiOutlineDown } from 'react-icons/ai'

import { useToggle, useListData, usePet, timeDiff, stringifyDate, patientName } from '../../modules'

import images from '../../images'

import Receive from '../reusable/receive'

import EntrySummary from './entrySummary'

const { TabPane } = Tabs
const { RangePicker } = DatePicker

const MedHistory = ({ attachedTo, patient }) => {
    let group = 'Medication'
    const [ranges, setRanges] = useState()
    const [string, setString] = useState()

    const [includeOthers, toggleOthers] = useToggle()

    const { user } = useSelector(state => state.application)

    const [
        { loading: itLoading, error: itError, fetch: fetchBillItems },
        { list: billItems },
        { page: itPage = 1, limit: itLimit = 10, navigate, Paginate }
    ] = useListData('billedItems', ranges ? {
        patient: patient?._id,
        attachedTo: includeOthers ? undefined : attachedTo?._id,
        string,
        group,
        from: ranges[0]?._d ?? null,
        to: ranges[1]?._d ?? null
    } : { string, group, patient: patient?._id, attachedTo: includeOthers ? undefined : attachedTo?._id, }, { notify: true }, 'items')

    const refresh = () => {
        navigate(1)
        fetchBillItems()
    }

    const searchSent = str => {
        if (str?.trim()?.length > 2 || !str) {
            setString(str)
            refresh()
        }
    }

    const selectRange = range => {
        setRanges(range)
        refresh()
    }

    const selectOthers = () => {
        toggleOthers()
        refresh()
    }

    return (
        <>
            <div className="sm:flex sm:justify-between sm:items-center mb-2">
                <span>
                    <input
                        onChange={e => searchSent(e.target.value)}
                        className="appearance-none block max-w-4xl bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                        id="grid-ssearch"
                        type="text"
                        placeholder="Search for item" />
                    <span>
                        <input
                            onChange={ selectOthers }
                            checked={ includeOthers }
                            type="checkbox"
                            className="form-checkbox cursor-pointer h-5 w-5" />
                        <span onClick={ selectOthers } className="text-gray-500 cursor-pointer text-xs italic ml-2">Show medication from outside this period</span>
                    </span>
                </span>
                {
                    includeOthers &&
                    <RangePicker onChange={selectRange} />
                }
            </div>
            <div className="">
                <Receive
                    progress
                    loading={itLoading}
                    error={itError}>
                    {
                        billItems.length > 0 ?
                            <div className="overflow-x-auto">
                                <table className="table-auto w-full">
                                    {/* Table header */}
                                    <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                        <tr>
                                            <th className="p-2">
                                                <div className="font-semibold text-center">No</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-left">Item</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-left">Quantity</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-left">Sent By</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-center"></div>
                                            </th>
                                        </tr>
                                    </thead>
                                    {/* Table body */}
                                    <tbody className="text-sm font-medium divide-y divide-gray-100">
                                        {
                                            billItems.map((item, key) =>
                                                <tr>
                                                    <td className="p-2">
                                                        <div className="text-center">{((itPage - 1) * itLimit) + (key + 1)}.</div>
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="">{item.name} {item.quantity > 1 && ` (${item.instructions})`}</div>
                                                        <p className="text-gray-500 my-0 text-xs italic">Sent On {stringifyDate(item.date)}</p>
                                                        <p className={`text-${item.seen ? 'green' : 'yellow'}-500 mt-0 text-xs italic mb-0`}>{item.seen ? 'Seen' : 'Pending'}</p>
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="">{item.quantity} { pluralize(item?.ref?.ref?.issuedIn ?? 'Units', item.quantity) }</div>
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="">{item.sentBy?.name}</div>
                                                    </td>
                                                    <td className="p-2">
                                                        <Dropdown
                                                            overlay={
                                                                <Menu>
                                                                    <Menu.Item onClick={() => null}>View</Menu.Item>
                                                                    {
                                                                        !item.seen && (item.sentBy._id === user?._id || user?.issuperuser) &&
                                                                        <>
                                                                            <Menu.Item onClick={() => null}>Edit</Menu.Item>
                                                                            <Menu.Item onClick={() => null}>Delete</Menu.Item>
                                                                        </>
                                                                    }
                                                                </Menu>
                                                            }>
                                                            <a className="ant-dropdown-link flex items-center h-10" onClick={e => e.preventDefault()}><span className="mr-2">Actions</span> <AiOutlineDown /></a>
                                                        </Dropdown>
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                </table>
                            </div>
                            :
                            <div className="w-full h-16 mt-4 flex items-center justify-center">
                                <p className="font-semibold text-base">No {pluralize(group.toLowerCase())} were found</p>
                            </div>
                    }
                </Receive>
                {Paginate}
            </div>
        </>
    )
}

const History = ({ group, attachedTo, category, patient, name, path }) => {
    const [ranges, setRanges] = useState()
    const [string, setString] = useState()

    const [includeOthers, toggleOthers] = useToggle()

    const { user } = useSelector(state => state.application)

    const [
        { loading: itLoading, error: itError, fetch: fetchBillItems },
        { list: billItems },
        { page: itPage = 1, limit: itLimit = 10, navigate, Paginate }
    ] = useListData('billedItems', ranges ? {
        patient: patient?._id,
        attachedTo: includeOthers ? undefined : attachedTo?._id,
        string,
        group,
        category,
        from: ranges[0]?._d ?? null,
        to: ranges[1]?._d ?? null
    } : { string, group, attachedTo: includeOthers ? undefined : attachedTo?._id, category, patient: patient?._id }, { notify: true }, 'items')

    const refresh = () => {
        navigate(1)
        fetchBillItems()
    }

    const searchSent = str => {
        if (str?.trim()?.length > 2 || !str) {
            setString(str)
            refresh()
        }
    }

    const selectRange = range => {
        setRanges(range)
        refresh()
    }

    const selectOthers = () => {
        toggleOthers()
        refresh()
    }

    return (
        <>
            <div className="sm:flex sm:justify-between sm:items-center mb-2">
                <span>
                    <input
                        onChange={e => searchSent(e.target.value)}
                        className="appearance-none block max-w-4xl bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                        id="grid-ssearch"
                        type="text"
                        placeholder="Search for item" />
                    <span>
                        <input
                            onChange={ selectOthers }
                            checked={ includeOthers }
                            type="checkbox"
                            className="form-checkbox cursor-pointer h-5 w-5" />
                        <span onClick={ selectOthers } className="text-gray-500 cursor-pointer text-xs italic ml-2">Show { name ?? group?.toLowerCase() ?? 'history' } from outside this period</span>
                    </span>
                </span>
                {
                    includeOthers &&
                    <RangePicker onChange={selectRange} />
                }
            </div>
            <div className="">
                <Receive
                    progress
                    loading={itLoading}
                    error={itError}>
                    {
                        billItems.length > 0 ?
                            <div className="overflow-x-auto">
                                <table className="table-auto w-full">
                                    {/* Table header */}
                                    <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                        <tr>
                                            <th className="p-2">
                                                <div className="font-semibold text-center">No</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-left">Item</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-left">Sent By</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-center"></div>
                                            </th>
                                        </tr>
                                    </thead>
                                    {/* Table body */}
                                    <tbody className="text-sm font-medium divide-y divide-gray-100">
                                        {
                                            billItems.map((item, key) =>
                                                <tr>
                                                    <td className="p-2">
                                                        <div className="text-center">{((itPage - 1) * itLimit) + (key + 1)}.</div>
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="">{item.name} {item.quantity > 1 && ` (x ${item.quantity})`}</div>
                                                        <p className="text-gray-500 my-0 text-xs italic">Sent On {stringifyDate(item.date)}</p>
                                                        <p className={`text-${item.seen ? 'green' : 'yellow'}-500 mt-0 text-xs italic mb-0`}>{item.seen ? 'Seen' : 'Pending'}</p>
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="">{item.sentBy?.name}</div>
                                                    </td>
                                                    <td className="p-2">
                                                        <Dropdown
                                                            overlay={
                                                                <Menu>
                                                                    <Link to={ `${path}patient/${item._id}`} ><Menu.Item>View</Menu.Item></Link>
                                                                    {
                                                                        !item.seen && (item.sentBy._id === user?._id || user?.issuperuser) &&
                                                                        <>
                                                                            <Menu.Item onClick={() => null}>Edit</Menu.Item>
                                                                            <Menu.Item onClick={() => null}>Delete</Menu.Item>
                                                                        </>
                                                                    }
                                                                </Menu>
                                                            }>
                                                            <a className="ant-dropdown-link flex items-center h-10" onClick={e => e.preventDefault()}><span className="mr-2">Actions</span> <AiOutlineDown /></a>
                                                        </Dropdown>
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                </table>
                            </div>
                            :
                            <div className="w-full h-16 mt-4 flex items-center justify-center">
                                <p className="font-semibold text-base">{ name === 'issued items' ? `No requests were made for ${patientName(patient?.name)}` : `${patientName(patient?.name)} wasn't sent for any ${ group?.toLowerCase() ?? category?.toLowerCase() ?? 'items' }` }</p>
                            </div>
                    }
                </Receive>
                {Paginate}
            </div>
        </>
    )
}

const Vitals = ({ patient }) => {
    const { id: entry } = useParams()

    return (
        <>
            {
                Array(3).fill().map(x =>
                    <div className="shadow-lg rounded-sm border mb-3 border-gray-200 px-4 pt-4 pb-1">
                        <span className="flex justify-between items-center">
                            <div className="flex mb-2">
                                <div className="mr-2 text-center p-2">
                                    <p className="font-semibold text-xl mb-1">75 Kg </p>
                                    <p className="font-light text-xs mb-0">Weight</p>
                                </div>
                                <div className="mr-2 text-center border-l-2 p-2 border-gray-200">
                                    <p className="font-semibold text-xl mb-1">140 cm</p>
                                    <p className="font-light text-xs mb-0">Height</p>
                                </div>
                                <div className="mr-2 text-center border-l-2 p-2 border-gray-200">
                                    <p className="font-semibold text-xl mb-1">30 Deg</p>
                                    <p className="font-light text-xs mb-0">Temperature</p>
                                </div>
                                <div className="mr-2 text-center border-l-2 p-2 border-gray-200">
                                    <p className="font-semibold text-xl mb-1">140 / 72 mmHg</p>
                                    <p className="font-light text-xs mb-0">Blood Pressure</p>
                                </div>
                                <div className="mr-2 text-center border-l-2 p-2 border-gray-200">
                                    <p className="font-semibold text-xl mb-1">25</p>
                                    <p className="font-light text-xs mb-0">Pulse</p>
                                </div>
                            </div>
                            <Link to={`/app/admissions/${entry}/vitals/2`}>
                                <button className="flex self-start btn mb-2 py-1 shadow-none bg-white hover:bg-gray-100 hover:text">View</button>
                            </Link>
                        </span>
                        <div className="flex justify-between border-t-2 mb-2 border-gray-200 w-full pt-2">
                            <p className="font-semibold italic text-xs mb-1">Taken by Reinhardt Collins</p>
                            <p className="font-light text-xs mb-0">{ stringifyDate(new Date()) }</p>
                        </div>
                    </div>
                )
            }
        </>
    )
}

const PatientHistory = ({ attachedTo, source }) => {
    const [patient, setPatient] = useState()
    const history = useHistory()
    const { id } = useParams()

    const [visible, toggleVisible] = useToggle(true)

    const [{ loading: pLoading, error: pError }] = usePet('patientFromEntry', { id }, { notify: true, callback: ({ data }) => setPatient(data) })

    const close = () => {
        toggleVisible()
        setTimeout(() => history.goBack(), 300)
    }

    return (
        <Receive
            progress
            loading={pLoading}
            error={pError}>
            {
                patient ?
                <Drawer
                    closable
                    maskClosable
                    visible={visible}
                    onClose={close}
                    width="50%">
                    <p className="font-extrabold text-3xl mb-0">Patient's Details</p>
                    <div className="flex items-center px-2 py-4">
                        <div className="w-28 h-28 flex items-center rounded-full justify-center overflow-hidden">
                            <img className="h-24 w-24 mx-auto" src={ images[`${patient?.gender?.toLowerCase()}_alt`] ?? images.male_alt } />
                        </div>
                        <div className="w-9/12 flex items-center">
                            <div className="w-10/12 flex flex-col leading-none pl-4">
                                <p className="text-2xl font-bold mb-0">{ patient?.name }</p>
                                <p className="text-sm my-1 font-light text-gray-700">
                                    { 
                                        timeDiff(patient.dob) < 0 ? 
                                            timeDiff(patient.dob, 'months') < 0 ? 
                                                timeDiff(patient.dob, 'weeks') < 0 ? 
                                                    timeDiff(patient.dob, 'days') < 0 ? 
                                                        timeDiff(patient.dob, 'hours')
                                                    : timeDiff(patient.dob, 'days')
                                                : timeDiff(patient.dob, 'weeks')
                                            : timeDiff(patient.dob, 'months')
                                        : timeDiff(patient.dob)
                                    } Years Old (Born on { stringifyDate(patient.dob, true) })
                                </p>
                                {
                                    patient.ipNo &&
                                    <p className="text-gray-500 text-xs mb-0 italic">Inpatient No: { patient.ipNo }</p>
                                }
                                {
                                    patient.opNo &&
                                    <p className="text-gray-500 text-xs mt-0 italic">Outpatient No: { patient.opNo < 1000 ? ('00000' + patient.opNo).slice(-4) : patient.opNo }</p>
                                }
                            </div>
                            <div className="w-2/12">
                                <div>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="text-gray-700" fill="currentColor" viewBox="0 0 24 24" width={24} height={24}><path fill="none" d="M0 0h24v24H0z" /><path d="M9.243 19H21v2H3v-4.243l9.9-9.9 4.242 4.244L9.242 19zm5.07-13.556l2.122-2.122a1 1 0 0 1 1.414 0l2.829 2.829a1 1 0 0 1 0 1.414l-2.122 2.121-4.242-4.242z" /></svg>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Tabs defaultActiveKey="0">
                        <TabPane key="0" tab="Details">
                            <div className="grid gap-3 md:grid-cols-2 sm:grid-cols-1">
                                <div className="flex flex-col justify-between shadow-lg rounded-sm border mb-3 border-gray-200 px-4 pt-3 pb-1">
                                    <div className="w-full px-3 mb-6 md:mb-0">
                                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                            Phone
                                        </label>
                                        <input
                                            // onKeyUp={ onKeyUp }
                                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                            id="grid-phone"
                                            type="text"
                                            disabled
                                            value={ patient?.phone }
                                            placeholder="phone" />
                                    </div> 
                                    <div className="w-full px-3 mb-6 md:mb-0">
                                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                            Email
                                        </label>
                                        <input
                                            // onKeyUp={ onKeyUp }
                                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                            id="grid-email"
                                            type="email"
                                            disabled
                                            value={ patient?.email }
                                            placeholder="email" />
                                    </div>                              
                                </div>
                                <div className="flex flex-col justify-between shadow-lg rounded-sm border mb-3 border-gray-200 px-4 pt-3 pb-1">
                                    <div className="w-full px-3 mb-6 md:mb-0">
                                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                            County
                                        </label>
                                        <input
                                            // onKeyUp={ onKeyUp }
                                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                            id="grid-county"
                                            type="text"
                                            disabled
                                            value={ patient?.county }
                                            placeholder="county" />
                                    </div> 
                                    <div className="w-full px-3 mb-6 md:mb-0">
                                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                            Locality
                                        </label>
                                        <input
                                            // onKeyUp={ onKeyUp }
                                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                            id="grid-locality"
                                            type="text"
                                            disabled
                                            value={ patient?.locality }
                                            placeholder="locality" />
                                    </div>                              
                                </div>
                            </div>
                            <div className="grid gap-3 md:grid-cols-2 sm:grid-cols-1">
                                <div className="flex flex-col justify-between shadow-lg rounded-sm border mb-3 border-gray-200 px-4 pt-3 pb-1">
                                    <div className="w-full px-3 mb-6 md:mb-0">
                                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                            Next Of Kin Name
                                        </label>
                                        <input
                                            // onKeyUp={ onKeyUp }
                                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                            id="grid-phone"
                                            type="text"
                                            disabled
                                            value={ patient?.nokName }
                                            placeholder="Next Of Kin Name" />
                                    </div> 
                                    <div className="w-full px-3 mb-6 md:mb-0">
                                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                            Next Of Kin RelationShip
                                        </label>
                                        <input
                                            // onKeyUp={ onKeyUp }
                                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                            id="grid-nokRship"
                                            type="text"
                                            disabled
                                            value={ patient?.nokRShip }
                                            placeholder="Next Of Kin RelationShip" />
                                    </div>                              
                                </div>
                                <div className="flex flex-col justify-between shadow-lg rounded-sm border mb-3 border-gray-200 px-4 pt-3 pb-1">
                                    <div className="w-full px-3 mb-6 md:mb-0">
                                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                        Next Of Kin Phone
                                        </label>
                                        <input
                                            // onKeyUp={ onKeyUp }
                                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                            id="grid-nokPhone"
                                            type="text"
                                            disabled
                                            value={ patient?.nokPhone }
                                            placeholder="Next Of Kin Phone" />
                                    </div> 
                                    <div className="w-full px-3 mb-6 md:mb-0">
                                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                            Locality
                                        </label>
                                        <input
                                            // onKeyUp={ onKeyUp }
                                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                            id="grid-locality"
                                            type="text"
                                            disabled
                                            value={ patient?.locality }
                                            placeholder="locality" />
                                    </div>                              
                                </div>
                            </div>
                        </TabPane>
                        <TabPane key="1" tab="Vitals">
                            <Vitals patient={ patient } />
                        </TabPane>
                        <TabPane key="2" tab="Doctor">
                            <div className="sm:flex sm:justify-between sm:items-center mb-2">
                                <span>
                                    <input
                                        // onChange={e => searchSent(e.target.value)}
                                        className="appearance-none block max-w-4xl bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                                        id="grid-ssearch"
                                        type="text"
                                        placeholder="Search for item" />
                                </span>
                                {/* {
                                    includeOthers && */}
                                    <RangePicker /*onChange={selectRange}*/ />
                                {/* } */}
                            </div>
                            <table className="table-auto w-full">
                                {/* Table header */}
                                <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                    <tr>
                                        <th className="p-2">
                                            <div className="font-semibold text-center">No</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-left">Type</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-left">Status</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-left">Seen By</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-center"></div>
                                        </th>
                                    </tr>
                                </thead>
                                {/* Table body */}
                                <tbody className="text-sm font-medium divide-y divide-gray-100">
                                    {
                                        Array(4).fill().map((request, key) =>
                                            <tr>
                                                <td className="p-2">
                                                    <div className="text-center">{key + 1}.</div>
                                                </td>
                                                <td className="p-2">
                                                    <div className="">Spine Consultation</div>
                                                    <p className="text-gray-500 text-xs italic">Sent on { stringifyDate(new Date()) }</p>
                                                </td>
                                                <td className="p-2">
                                                    <div className='text-green-500'>Seen</div>
                                                </td>
                                                <td className="p-2">
                                                    <div>Robin Charles</div>
                                                    <p className="text-gray-500 text-xs italic">{ stringifyDate(new Date()) }</p>
                                                </td>
                                                <td className="p-2 text-center">
                                                    <Link to="/">
                                                        <button className="text-white hisani-btn-green-light font-medium ml-2 rounded-lg text-sm px-3 py-1.5 text-center">View</button>
                                                    </Link>
                                                </td>
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </table>
                        </TabPane>
                        <TabPane key="3" tab="Investigations" >
                            <History
                                path={ source }
                                group="Investigations"
                                attachedTo={ attachedTo }
                                patient={ patient } />
                        </TabPane>
                        <TabPane key="4" tab="Treatments">
                            <History
                                path={ source }
                                group="Treatment"
                                attachedTo={ attachedTo }
                                patient={ patient } />
                        </TabPane>
                        <TabPane key="5" tab="Medication">
                            <MedHistory 
                                path={ source }
                                attachedTo={ attachedTo }
                                patient={ patient } />
                        </TabPane>
                        <TabPane key="6" tab="Issued Items">
                            <History
                                path={ source }
                                name="issued items"
                                group="Non-Medical Billed Items"
                                attachedTo={ attachedTo }
                                patient={ patient } />
                        </TabPane>
                        <TabPane key="7" tab="Surgery">
                            <History
                                path={ source }
                                group="Surgery"
                                name="surgeries"
                                patient={ patient } />
                        </TabPane>
                    </Tabs>
                    <Switch>
                        <Route path={ `${source}patient/:ref` }>
                            <EntrySummary path={ `${source}patient` }/>
                        </Route>
                    </Switch>
                </Drawer>
                : null
            }
        </Receive>
    )
}

export default PatientHistory